import {useEffect, useMemo, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Alert, Box, Button, CircularProgress, Typography, useTheme} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import PageHeader from "../../../components/PageHeader";
import {useCurrentUser} from "../../../hooks/useCurrentUser";
import {useBusinessUnitCodes} from "../../../hooks/useBusinessUnitCodes";
import {useUserTypes} from "../../../hooks/useUserTypes";
import {useClaimCategories} from "../../../hooks/useClaimCategories";
import {useAvailableGroups} from "../../../hooks/useAvailableGroups";
import {useAuthUserCreate} from "../../../hooks/useAuthUserCreate";
import {useAuthUser} from "../../../hooks/useAuthUser";
import {getUserByLogin} from "../../../services/UserService";
import {generateRandomNumber} from "../../../util/NumberUtil";
import {colorForIndex} from "./palette";
import {getStep4Config, step4HasFields} from "./userTypeConfig";
import Step1Login from "./steps/Step1Login";
import Step2Profile from "./steps/Step2Profile";
import Step3Groups from "./steps/Step3Groups";
import Step4Details from "./steps/Step4Details";
import Step5Review from "./steps/Step5Review";

const STEPS = [
    {n: 1, label: "Login"},
    {n: 2, label: "Profile & Classification"},
    {n: 3, label: "Groups & Permissions"},
    {n: 4, label: "Additional Details"},
    {n: 5, label: "Review"},
];

const getInitialDraft = () => ({
    login: "",
    setupMethod: "scratch",
    firstName: "",
    lastName: "",
    countryCode: "",
    phoneNumber: "",
    businessUnitCode: "",
    userTypeId: "",
    groupIds: null,        // null = not yet defaulted for the current BU + User Type
    orderPin: String(generateRandomNumber(4)),
    shiptoId: "",
    billtoId: "",
    retailLocationId: "",
    adminPortalAccess: false,
    salesRegionId: "",     // region id (user types with a sales region)
    salesTerritories: [],  // [{id, name, label, region, regionName}] (user types with sales territories)
    facilities: [],        // [{id, name}] (user types with facilities)
});

const CreateUserWizard = () => {
    const theme = useTheme();
    const navigate = useNavigate();

    const [draft, setDraft] = useState(getInitialDraft);
    const [step, setStep] = useState(1);

    // Login-check state (kept outside the draft — it's transient lookup state, not user input).
    const [loginStatus, setLoginStatus] = useState("unchecked"); // unchecked | available | taken
    const [existingUser, setExistingUser] = useState(null);
    const [checking, setChecking] = useState(false);
    const [loginError, setLoginError] = useState("");

    const [submitError, setSubmitError] = useState("");
    const [success, setSuccess] = useState(null); // {id, login}

    // FE-7 — clone (Step 1) and copy-groups (Step 3) state.
    const [cloneUserId, setCloneUserId] = useState(null);
    const [cloneSource, setCloneSource] = useState(null);   // the picked search row (for the banner name)
    const [cloneSummary, setCloneSummary] = useState(null); // {sourceName, userTypeName, businessUnitCode, groupNames[], territoryCount}
    const [copySummary, setCopySummary] = useState(null);   // {sourceName, groupNames[]}
    const [pendingGroupReconcile, setPendingGroupReconcile] = useState(false);
    const appliedCloneRef = useRef(null);

    const [currentUser] = useCurrentUser();
    const businessUnits = useBusinessUnitCodes(true, 0);
    const [userTypes] = useUserTypes({managedByUserId: currentUser?.id, enabled: !!currentUser?.id});
    const [categories] = useClaimCategories();

    // Groups available for the chosen (User Type, Business Unit) — a single call returning each group
    // with its default flag and claims (TMMFX-2170 Change 4).
    const {groups: availableGroups, isLoading: groupsLoading} = useAvailableGroups(
        draft.userTypeId, draft.businessUnitCode
    );

    // Full authorization-aware detail for the user being cloned (API-2). Drives the Step-1 apply.
    const {user: cloneDetail, isLoading: cloneLoading, isError: cloneError} = useAuthUser(cloneUserId, !!cloneUserId);

    // Decorated with a stable per-group color for the permission-preview dots.
    const availableGroupObjs = useMemo(
        () => availableGroups.map((g, i) => ({
            id: g.id,
            name: g.name,
            color: colorForIndex(i),
            claims: g.claims ?? [],
        })),
        [availableGroups]
    );
    const selectedGroupObjs = useMemo(
        () => availableGroupObjs.filter((g) => (draft.groupIds ?? []).includes(g.id)),
        [availableGroupObjs, draft.groupIds]
    );

    const defaultGroupId = useMemo(
        () => availableGroups.find((g) => g.isDefault)?.id ?? null,
        [availableGroups]
    );
    const cfg = getStep4Config(draft.userTypeId);
    const grantsAdmin = selectedGroupObjs.some((g) => (g.claims ?? []).some((c) => c.claimValue === "AccessAdminPortal"));
    const userTypeDisplayName = useMemo(
        () => (userTypes ?? []).find((u) => u.id === Number(draft.userTypeId))?.displayName ?? "",
        [userTypes, draft.userTypeId]
    );

    // Auto-select the default group once availability has loaded for the current BU + User Type.
    useEffect(() => {
        if (draft.groupIds === null && draft.userTypeId && draft.businessUnitCode && !groupsLoading) {
            setDraft((prev) => ({...prev, groupIds: defaultGroupId != null ? [defaultGroupId] : []}));
        }
    }, [draft.groupIds, draft.userTypeId, draft.businessUnitCode, groupsLoading, defaultGroupId]);

    // FE-7: reconcile a primed (cloned / copied) concrete group selection against the current combo's
    // availability — drop ids not available for the (User Type, Business Unit), and fall back to the
    // combo default if that empties the set. Only fires when explicitly requested by a clone / copy.
    useEffect(() => {
        if (!pendingGroupReconcile || groupsLoading) return;
        const availableIds = availableGroups.map((g) => g.id);
        setDraft((prev) => {
            // Only a concrete primed selection is reconciled. A null selection belongs to the
            // auto-select effect; forcing it to [] here would suppress that default (e.g. if the
            // manager switches back to "Start from scratch" before the cloned combo finished loading).
            if (prev.groupIds === null) return prev;
            let kept = prev.groupIds.filter((id) => availableIds.includes(id));
            if (kept.length === 0) kept = defaultGroupId != null ? [defaultGroupId] : [];
            return {...prev, groupIds: kept};
        });
        setPendingGroupReconcile(false);
    }, [pendingGroupReconcile, groupsLoading, availableGroups, defaultGroupId]);

    // FE-7: once the cloned user's full detail (API-2) arrives, prime the draft in a single apply —
    // classification + groups + territories + admin access. Uses setDraft directly (not setField) so the
    // combined patch bypasses the BU/UserType group-reset. Identity / login / order pin are left as-is.
    useEffect(() => {
        if (!cloneUserId || !cloneDetail || appliedCloneRef.current === cloneUserId) return;
        appliedCloneRef.current = cloneUserId;
        const groupNames = (cloneDetail.groups ?? []).map((g) => g.name);
        const territoryCount = (cloneDetail.salesTerritoryIds ?? []).length;
        setDraft((prev) => ({
            ...prev,
            setupMethod: "clone",
            businessUnitCode: cloneDetail.businessUnitCode ?? "",
            userTypeId: cloneDetail.userTypeId ?? "",
            countryCode: cloneDetail.countryCode ?? "",
            groupIds: (cloneDetail.groups ?? []).map((g) => g.id),
            salesRegionId: cloneDetail.salesRegionId ?? "",
            salesTerritories: (cloneDetail.salesTerritoryIds ?? []).map((id) => ({id})),
            adminPortalAccess: !!cloneDetail.adminPortalAccess,
        }));
        setPendingGroupReconcile(true);
        const utName = (userTypes ?? []).find((u) => u.id === Number(cloneDetail.userTypeId))?.displayName ?? "";
        setCloneSummary({
            sourceName: cloneSource ? [cloneSource.firstName, cloneSource.lastName].filter(Boolean).join(" ") : "",
            userTypeName: utName,
            businessUnitCode: cloneDetail.businessUnitCode ?? "",
            groupNames,
            territoryCount,
        });
    }, [cloneUserId, cloneDetail, cloneSource, userTypes]);

    const setFields = (patch) => setDraft((prev) => ({...prev, ...patch}));

    const setField = (key, value) => {
        setDraft((prev) => {
            const next = {...prev, [key]: value};
            // Changing BU or User Type invalidates the group selection and downstream detail fields.
            if (key === "businessUnitCode" || key === "userTypeId") {
                next.groupIds = null;
                next.salesRegionId = "";
                next.salesTerritories = [];
                next.facilities = [];
            }
            if (key === "businessUnitCode") {
                next.countryCode = "";
            }
            if (key === "userTypeId") {
                next.adminPortalAccess = !!getStep4Config(value).adminDefaultOn;
            }
            // Ship-to is scoped by business unit and, for user types with a Bill-to, by that Bill-to; a change
            // to either (or to the user type) can leave the chosen shipto invalid.
            if ((key === "businessUnitCode" || key === "userTypeId" || key === "billtoId") && value !== prev[key]) {
                next.shiptoId = "";
            }
            return next;
        });
    };

    // ---- FE-7 setup method / clone / copy ----
    const selectSetupMethod = (method) => {
        if (method === draft.setupMethod) return;
        if (method === "scratch") {
            // Switching back to scratch clears everything the clone primed; keep the verified login.
            setDraft({...getInitialDraft(), login: draft.login, setupMethod: "scratch"});
            setCloneUserId(null);
            setCloneSource(null);
            setCloneSummary(null);
            setPendingGroupReconcile(false);
            appliedCloneRef.current = null;
        } else {
            setField("setupMethod", method);
        }
    };

    const cloneFromUser = (row) => {
        appliedCloneRef.current = null;
        setCloneSummary(null);
        setCloneSource(row);
        setCloneUserId(row.id);
    };

    // Step 3 — replace the current group selection with the source user's groups, then reconcile against
    // availability for the current (User Type, Business Unit). Touches only the group selection.
    const copyGroupsFromUser = (row) => {
        const ids = (row.groups ?? []).map((g) => g.id);
        setDraft((prev) => ({...prev, groupIds: ids}));
        setPendingGroupReconcile(true);
        setCopySummary({
            sourceName: [row.firstName, row.lastName].filter(Boolean).join(" "),
            groupNames: (row.groups ?? []).map((g) => g.name),
        });
    };

    // ---- login check ----
    const checkLogin = () => {
        if (!draft.login) return;
        setChecking(true);
        setLoginError("");
        getUserByLogin(draft.login)
            .then((data) => { setExistingUser(data); setLoginStatus("taken"); })
            .catch((err) => {
                const s = err.response?.status;
                if (err.response && s !== undefined && s < 500) {
                    setLoginStatus("available");
                    setExistingUser(null);
                } else {
                    setLoginError(err.response?.data?.message || "Error checking login. Please try again.");
                }
            })
            .finally(() => setChecking(false));
    };

    const resetLogin = (keepValue) => {
        setLoginStatus("unchecked");
        setExistingUser(null);
        setLoginError("");
        if (!keepValue) setField("login", "");
    };

    // ---- navigation ----
    const nextStep = (from) => {
        let n = from + 1;
        if (n === 4 && !step4HasFields(draft.userTypeId)) n = 5;
        return n;
    };
    const prevStep = (from) => {
        let n = from - 1;
        if (n === 4 && !step4HasFields(draft.userTypeId)) n = 3;
        return n;
    };

    const canContinue = (s) => {
        switch (s) {
            case 1: return loginStatus === "available";
            case 2: return !!(draft.firstName && draft.lastName && draft.countryCode && draft.businessUnitCode && draft.userTypeId);
            case 3: return (draft.groupIds ?? []).length >= 1;
            case 4: return !cfg.shiptoRequired || !!draft.shiptoId;
            default: return true;
        }
    };

    const handleContinue = () => {
        if (step === 5) { handleSubmit(); return; }
        if (!canContinue(step)) return;
        setStep(nextStep(step));
    };
    const handleBack = () => setStep(prevStep(step));

    const goToStep = (n) => setStep(n);

    // ---- submit ----
    const createMutation = useAuthUserCreate();

    const buildPayload = () => {
        const adminEnabled = grantsAdmin ? true : !!draft.adminPortalAccess;
        const individualClaims = [];
        if (cfg.adminAccess && adminEnabled && !grantsAdmin) {
            individualClaims.push({claimType: "permission", claimValue: "AccessAdminPortal"});
        }
        const payload = {
            login: draft.login,
            firstName: draft.firstName,
            lastName: draft.lastName,
            countryCode: draft.countryCode || undefined,
            phoneNumber: draft.phoneNumber || undefined,
            businessUnitCode: draft.businessUnitCode,
            userTypeId: Number(draft.userTypeId),
            // email intentionally left null — the wizard has no email field and the BE does not
            // require it. (Legacy email column is nullable.)
            groupIds: draft.groupIds ?? [],
            individualClaims,
            adminPortalAccess: cfg.adminAccess ? adminEnabled : false,
        };
        if (cfg.orderPin && draft.orderPin) payload.orderPin = String(draft.orderPin);
        if (cfg.shipto && draft.shiptoId) payload.shiptoId = draft.shiptoId;
        if (cfg.billto && draft.billtoId) payload.billtoId = draft.billtoId;
        if (cfg.retailLoc && draft.retailLocationId) payload.retailLocationId = Number(draft.retailLocationId);
        payload.salesRegionId = cfg.hasSalesRegion && draft.salesRegionId ? Number(draft.salesRegionId) : null;
        payload.salesTerritoryIds = cfg.hasSalesTerritories ? (draft.salesTerritories ?? []).map((t) => Number(t.id)) : [];
        payload.facilityIds = cfg.hasFacilities ? (draft.facilities ?? []).map((f) => Number(f.id)) : [];
        return payload;
    };

    const handleSubmit = () => {
        setSubmitError("");
        createMutation.mutate(buildPayload(), {
            onSuccess: (resp) => setSuccess({id: resp.data?.id, login: resp.data?.login || draft.login}),
            onError: (err) => setSubmitError(
                err.response?.data?.message || err.response?.data?.title || "Failed to create user. Please try again."
            ),
        });
    };

    const resetWizard = () => {
        setDraft(getInitialDraft());
        setStep(1);
        setLoginStatus("unchecked");
        setExistingUser(null);
        setLoginError("");
        setSubmitError("");
        setSuccess(null);
        setCloneUserId(null);
        setCloneSource(null);
        setCloneSummary(null);
        setCopySummary(null);
        setPendingGroupReconcile(false);
        appliedCloneRef.current = null;
    };

    const ctx = {
        draft, setField, setFields,
        currentUser, businessUnits, userTypes,
        availableGroupObjs, selectedGroupObjs, categories, groupsLoading, defaultGroupId,
        cfg, grantsAdmin, businessUnitCode: draft.businessUnitCode,
        userTypeDisplayName, goToStep,
        loginStatus, existingUser, checking, loginError, checkLogin, resetLogin,
        setupMethod: draft.setupMethod, selectSetupMethod,
        cloneFromUser, cloneLoading, cloneError, cloneSummary,
        copyGroupsFromUser, copySummary,
        onEditUser: () => existingUser && navigate(`/users/${existingUser.id}`),
    };

    // ---------- success state ----------
    if (success) {
        return (
            <Box sx={{width: "100%", height: "100%", display: "flex", flexDirection: "column"}}>
                <PageHeader title="Create User" />
                <Box sx={{flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 4}}>
                    <Box sx={{textAlign: "center", maxWidth: 480}}>
                        <CheckIcon sx={{fontSize: 56, color: "success.main"}} />
                        <Typography variant="h6" sx={{mt: 1}}>User created</Typography>
                        <Typography sx={{color: "text.secondary", mt: 1}}>
                            <b>{success.login}</b> has been created.
                        </Typography>
                        <Box sx={{display: "flex", gap: 1, justifyContent: "center", mt: 3}}>
                            <Button variant="contained" onClick={() => navigate(`/users/${success.id}`)}>View User</Button>
                            <Button onClick={resetWizard}>Create Another User</Button>
                            <Button onClick={() => navigate("/users")}>Back to Users</Button>
                        </Box>
                    </Box>
                </Box>
            </Box>
        );
    }

    const renderStep = () => {
        switch (step) {
            case 1: return <Step1Login {...ctx} />;
            case 2: return <Step2Profile {...ctx} />;
            case 3: return <Step3Groups {...ctx} />;
            case 4: return <Step4Details {...ctx} />;
            case 5: return <Step5Review {...ctx} />;
            default: return null;
        }
    };

    const stepSkipped = (n) => n === 4 && !step4HasFields(draft.userTypeId);

    return (
        <Box sx={{width: "100%", height: "100%", display: "flex", flexDirection: "column"}}>
            <PageHeader title="Create User">
                <Box sx={{display: "flex", justifyContent: "flex-end"}}>
                    <Button onClick={() => navigate("/users")}>Cancel</Button>
                </Box>
            </PageHeader>

            {/* Step rail */}
            <Box sx={{display: "flex", alignItems: "center", justifyContent: "center", gap: 1, px: 3, py: 2, borderBottom: `1px solid ${theme.palette.divider}`, flexWrap: "wrap"}}>
                {STEPS.map((s, idx) => {
                    const skipped = stepSkipped(s.n);
                    const active = s.n === step;
                    const done = s.n < step && !skipped;
                    const clickable = done;
                    return (
                        <Box key={s.n} sx={{display: "flex", alignItems: "center"}}>
                            <Box
                                onClick={clickable ? () => goToStep(s.n) : undefined}
                                sx={{
                                    display: "flex", alignItems: "center", gap: 1,
                                    cursor: clickable ? "pointer" : "default",
                                    opacity: skipped ? 0.4 : 1,
                                }}
                            >
                                <Box sx={{
                                    width: 26, height: 26, borderRadius: "50%", flex: "0 0 26px",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    fontSize: "0.78rem", fontWeight: 700,
                                    backgroundColor: active ? theme.palette.secondary.main : (done ? theme.palette.secondary.main : theme.palette.background.secondary),
                                    color: active || done ? "white" : "text.secondary",
                                    border: `1px solid ${active || done ? theme.palette.secondary.main : theme.palette.divider}`,
                                }}>
                                    {done ? <CheckIcon sx={{fontSize: 16}} /> : s.n}
                                </Box>
                                <Typography sx={{fontSize: "0.8rem", fontWeight: active ? 700 : 500, color: active ? "text.primary" : "text.secondary"}}>
                                    {s.label}{skipped ? " (skipped)" : ""}
                                </Typography>
                            </Box>
                            {idx < STEPS.length - 1 && <Box sx={{width: 24, height: 1, backgroundColor: theme.palette.divider, mx: 1}} />}
                        </Box>
                    );
                })}
            </Box>

            {/* Step content */}
            <Box sx={{flex: 1, minHeight: 0, overflowY: "auto", p: 3, backgroundColor: "white"}}>
                {renderStep()}
                {submitError && <Alert severity="error" sx={{mt: 2, maxWidth: 820}}>{submitError}</Alert>}
            </Box>

            {/* Footer navigation */}
            <Box sx={{display: "flex", justifyContent: "space-between", px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}`}}>
                <Button onClick={handleBack} disabled={step === 1}>Back</Button>
                <Button
                    variant="contained"
                    onClick={handleContinue}
                    disabled={!canContinue(step) || createMutation.isPending}
                >
                    {step === 5
                        ? (createMutation.isPending ? <><CircularProgress size={16} sx={{mr: 1}} /> Creating…</> : "Create User")
                        : "Continue"}
                </Button>
            </Box>
        </Box>
    );
};

export default CreateUserWizard;
